import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { CircleAlert, CircleCheck, Info, X } from 'lucide-react';
import styles from './Feedback.module.css';

/* ── Toasts ──────────────────────────────────────────────────────────────── */

type ToastKind = 'success' | 'error' | 'info';
type Toast = { id: number; kind: ToastKind; title: string; body?: string };

type ConfirmRequest = {
  title: string;
  body?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
};

type FeedbackApi = {
  toast: (kind: ToastKind, title: string, body?: string) => void;
  confirm: (request: ConfirmRequest) => Promise<boolean>;
};

const FeedbackContext = createContext<FeedbackApi | null>(null);

let nextToastId = 1;

export function FeedbackProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [confirmState, setConfirmState] = useState<
    (ConfirmRequest & { resolve: (value: boolean) => void }) | null
  >(null);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((item) => item.id !== id));
  }, []);

  const toast = useCallback(
    (kind: ToastKind, title: string, body?: string) => {
      const id = nextToastId++;
      setToasts((current) => [...current, { id, kind, title, body }]);
      window.setTimeout(() => dismiss(id), kind === 'error' ? 7000 : 4500);
    },
    [dismiss]
  );

  const confirm = useCallback(
    (request: ConfirmRequest) =>
      new Promise<boolean>((resolve) => {
        setConfirmState({ ...request, resolve });
      }),
    []
  );

  const settle = (value: boolean) => {
    confirmState?.resolve(value);
    setConfirmState(null);
  };

  const api = useMemo<FeedbackApi>(() => ({ toast, confirm }), [toast, confirm]);

  return (
    <FeedbackContext.Provider value={api}>
      {children}

      <div className={styles.toastRegion} role="status" aria-live="polite">
        {toasts.map((item) => (
          <div key={item.id} className={`${styles.toast} ${styles[item.kind]}`}>
            <span className={styles.toastIcon}>
              {item.kind === 'success' ? (
                <CircleCheck size={18} />
              ) : item.kind === 'error' ? (
                <CircleAlert size={18} />
              ) : (
                <Info size={18} />
              )}
            </span>
            <div className={styles.toastBody}>
              <strong>{item.title}</strong>
              {item.body ? <p>{item.body}</p> : null}
            </div>
            <button
              type="button"
              className={styles.toastClose}
              onClick={() => dismiss(item.id)}
              aria-label="Fermer la notification"
            >
              <X size={15} />
            </button>
          </div>
        ))}
      </div>

      {confirmState ? (
        <div
          className={styles.confirmOverlay}
          role="dialog"
          aria-modal="true"
          aria-label={confirmState.title}
          onClick={() => settle(false)}
        >
          <div className={styles.confirmCard} onClick={(event) => event.stopPropagation()}>
            <h2 className={styles.confirmTitle}>{confirmState.title}</h2>
            {confirmState.body ? <p className={styles.confirmBody}>{confirmState.body}</p> : null}
            <div className={styles.confirmActions}>
              <button type="button" className="btn btn--outline" onClick={() => settle(false)}>
                {confirmState.cancelLabel ?? 'Annuler'}
              </button>
              <button
                type="button"
                className={`btn ${confirmState.destructive ? styles.destructive : 'btn--primary'}`}
                onClick={() => settle(true)}
                autoFocus
              >
                {confirmState.confirmLabel ?? 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </FeedbackContext.Provider>
  );
}

export function useFeedback(): FeedbackApi {
  const context = useContext(FeedbackContext);
  if (!context) throw new Error('useFeedback must be used inside <FeedbackProvider>');
  return context;
}
